import {useConfigStore} from "~/store/config";
import {useUiStore} from "~/store/ui";
import useAlgoliaSearch from "~/composables/useAlgoliaSearch";
import type {SellerClub, SellerPerson} from "~/constants/types/seller";

export default defineNuxtRouteMiddleware(async(to) => {
  // Skip middleware for API routes
  if (to.path.startsWith('/api/')) {
    return;
  }
  
  // skip middleware on client side entirely
  if (import.meta.client) return;

  const config = useConfigStore();
  const uiStore = useUiStore();
  const { algoliaClubSearch, algoliaSellerSearch } = useAlgoliaSearch();
  const url = to.fullPath;
  const { sellerCookieOptions } = useCookieConfig();
  const clubCookie = useCookie<SellerClub>('club', sellerCookieOptions);
  const sellerCookie = useCookie<SellerPerson>('seller', sellerCookieOptions);
  const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions);
  const invoiceRejected = useCookie<boolean>('invoiceRejected', { ...sellerCookieOptions, default: () => false });
  let sellerId = null as null | string;
  if (to.query?.seller === 'true') {
    config.isSeller = true;
    isSeller.value = true;
    //if (!sellerCookie?.value || !clubCookie?.value) {
    uiStore.showSellerDialog = true;
    uiStore.sellerDialogSettings.sellerMissing = true;
    //}
  } else if (((url.includes('seller') || to.query?.sellerid) && !to.query?.seller) || (sellerCookie.value)) {
    if (url.includes('seller') || to.query?.sellerid) {
      const splitRoute = url.split('/');
      if (to.query?.sellerid) {
        sellerId = to.query.sellerid as string;
      } else if (to.query?.sellerId) {
        sellerId = to.query.sellerId as string;
      } else {
        if (splitRoute[3] && splitRoute[3] !== '') {
          sellerId = splitRoute[3].substring(0, 8);
        }
      }
    } else if (sellerCookie.value) {
      sellerId = sellerCookie.value.WebshopToken;
    }
      
    console.log('sellerId: ' + sellerId);
    if (sellerId) {
      const sellerRes = await algoliaSellerSearch(sellerId);
      const today = Date.parse(new Date());
      
      // Check if fallback index was used
      const currentBaseIndex = config.algoliaBaseIndex.replace('fi-sv', 'fi');
      const usedFallback = sellerRes.usedIndex !== currentBaseIndex;
      
      if (usedFallback) {
        console.log(`[Seller] Found in fallback index (${sellerRes.usedIndex}), switching market...`);
        
        // Determine target country and language from used index
        let targetLang: string;
        let targetMarketQuery: { country: string, language?: string };
        
        if (sellerRes.usedIndex.includes('_fi')) {
          // Swedish visitor found Finnish seller → redirect to Finnish-Swedish
          targetLang = 'fi-se';
          targetMarketQuery = { country: 'FI', language: 'fi-sv' };
        } else {
          // Finnish visitor found Swedish seller → redirect to Swedish
          targetLang = 'se';
          targetMarketQuery = { country: 'SE' };
        }
        
        // Find matching market in config
        const targetMarket = config.markets.find(m => {
          const countryMatch = m.country.toUpperCase() === targetMarketQuery.country;
          if (targetMarketQuery.language) {
            return countryMatch && m.language.toLowerCase() === targetMarketQuery.language;
          }
          return countryMatch;
        });
        
        if (targetMarket) {
          console.log(`[Seller] Switching to ${targetLang.toUpperCase()} market`);
          config.market = targetMarket;
          
          // Construct new URL with correct language prefix
          const sellerId = sellerRes.hits[0].WebshopToken;
          const fullPath = `/${targetLang}/seller/${sellerId}`;
          
          console.log(`[Seller] Redirecting from ${to.path} to ${fullPath}`);
          return navigateTo(fullPath, { redirectCode: 302 });
        } else {
          console.warn(`[Seller] Could not find market for: ${JSON.stringify(targetMarketQuery)}`);
        }
      }
      
      if (sellerRes.hits && sellerRes.hits.length === 1) {
        // Found a seller, checking that club is active
        // Use the same index that found the seller for club search
        const clubRes = await algoliaClubSearch(sellerRes.hits[0].RoundID, sellerRes.usedIndex);
        if (clubRes) {

          if (clubRes.length !== 1 || !clubRes[0].Active) {
            // Club is not active
            uiStore.sellerDialogSettings.clubInactive = true;
            uiStore.showSellerDialog = true;
          } else {
            console.log(clubRes[0]);
            // We have both our seller and our club
            const openShop = ref(false);
            if (clubRes[0].NoDeadlineDate) {
              openShop.value = true;
            } else if (clubRes[0].DeadlineDate?.length > 0) {
              let closingDate = Date.parse(clubRes[0].DeadlineDate);
              closingDate += 86400000; // add 24 hours to open also closing date
              if (closingDate >= today) {
                openShop.value = true;
              }
            }
            if (!openShop.value) {
              // Club is past deadline date
              uiStore.sellerDialogSettings.clubPastDeadline = true;
              isSeller.value = true;
              invoiceRejected.value = true;
              if (!to.query?.id != '') {
                // Don't show dialog if it's a create cart link
                uiStore.showSellerDialog = true;
              }
            } else if (!to.query?.id != '') { 
              invoiceRejected.value = false;
            }
            config.clubCookie = clubRes[0];
            config.sellerCookie = sellerRes.hits[0];
            config.isSeller = true;
            clubCookie.value = clubRes[0];
            sellerCookie.value = sellerRes.hits[0];
            isSeller.value = true;
          }
        } else {
          console.log('Club error');
          uiStore.sellerDialogSettings.clubError = true;
          uiStore.showSellerDialog = true;
          isSeller.value = true;
        }
      } else {
        console.log('Seller Error');
        uiStore.sellerDialogSettings.sellerError = true;
        uiStore.showSellerDialog = true;
        isSeller.value = true;
      }
    }
  } else if (config.sellerCookie) {
    console.log('Getting seller info from config');
  } else {
    console.log('Cookies are not set.');
  }

});
